import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { AddCircleOutline, Delete, DeleteOutline, Edit, PictureAsPdf, Refresh } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import TableView from '../../components/TableView';
import ConfirmDialog from '../../components/ConfirmDialog';
import ExportMenuButton from '../../components/ExportMenuButton';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useData } from '../../hooks/useData';
import { periodRequestConfig } from '../../hooks/useDateRange';
import { Util } from '../../util/Util';
import { LocalStorageKeys } from '../../util/constants';
import type { ExpenseRow } from './expenseTypes';
import { expenseColumns } from './expenseColumns';
import { useExpenseDelete } from './useExpenseDelete';
import ExpenseModal from './ExpenseModal';
import ExpenseReport from './ExpenseReport';
import ExpenseBatchReport from './ExpenseBatchReport';
import { canAmend, canRecord } from '../../util/access';

const Expense = () => {
  const { api, access } = useAuth();
  const { queueReRun, t } = useUI();
  const {
    data,
    lastUpdate,
    loadFromIndexedDB,
    saveFromServer,
    dateRange: period,
    setDateRange: setPeriod,
    loading,
    setLoading,
  } = useData<ExpenseRow>(LocalStorageKeys.EXPENSE_RECORDS);
  const [postOpen, setPostOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRow | null>(null);
  const [batchEdit, setBatchEdit] = useState(false);
  const [toView, setToView] = useState<ExpenseRow | null>(null);
  const [toViewBatch, setToViewBatch] = useState<string | null>(null);
  const firstFetchRef = useRef(false);
  const canPost = canRecord(access, 'expense');
  const canEdit = canAmend(access, 'expense')
    || canRecord(access, 'expense');
  const toastIdRef = useRef<string | number>(-1);

  const fetchData = async () => {
    setLoading(true);
    const res = await api.get('/expenses', periodRequestConfig(period));
    if (res?.status === 200) {
      const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];
      saveFromServer(rows, period);
      toast.update(toastIdRef.current, {
        render: t('common:toast.success.refresh'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchData);
    } else {
      toast.dismiss(toastIdRef.current);
    }
    setLoading(false);
  };

  const {
    canDelete,
    canDeleteBulk,
    toDelete,
    toDeleteBulk,
    setToDelete,
    setToDeleteBulk,
    handleDelete,
    handleBulkDelete,
    requestRowDelete,
    requestSelectionDelete,
    isConfirmOpen,
  } = useExpenseDelete(fetchData, toastIdRef);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadFromIndexedDB();
      if (cancelled || firstFetchRef.current) return;
      firstFetchRef.current = true;
      if (!loaded) void fetchData();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sharedBatches = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of data || []) {
      const batch = String(row.batch || '');
      if (!batch) continue;
      counts.set(batch, (counts.get(batch) || 0) + 1);
    }
    return counts;
  }, [data]);

  const isSharedBatch = (row: ExpenseRow) =>
    Boolean(row.batch) && (sharedBatches.get(String(row.batch)) || 0) > 1;

  const openEdit = (row: ExpenseRow, nextBatch = false) => {
    setEditing(row);
    setBatchEdit(nextBatch);
    setPostOpen(true);
  };

  const columns = useMemo(
    () => expenseColumns(t, {
      onRefClick: (row) => setToView(row),
      onBatchClick: (row) => setToViewBatch(String(row.batch || '')),
      batchCounts: sharedBatches,
    }),
    [t, sharedBatches],
  );

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TableView
				stateKey={LocalStorageKeys.EXPENSE_RECORDS}
				posting="expense"
          dataScope="expense"
          header={t('common:expense.title')}
          data={data}
          columns={columns}
          lastUpdated={lastUpdate}
          period={period}
          setDateRangeFilter={setPeriod}
          isLoading={loading}
          hideColumns={['batch']}
          totals={[{ label: t('common:tables.amount'), accessorFn: (row: ExpenseRow) => Number(row.amount || 0) }]}
          renderTopToolbarActions={(table) => (
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Tooltip title={t('common:ui.tooltips.refreshData')}>
                <IconButton onClick={() => void fetchData()} disabled={loading} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canPost && (
                <Tooltip title={t('common:expense.postTitle')}>
                  <IconButton color="success" onClick={() => { setEditing(null); setBatchEdit(false); setPostOpen(true); }}>
                    <AddCircleOutline />
                  </IconButton>
                </Tooltip>
              )}
              {canEdit && table.getSelectedRowModel().flatRows.length === 1 && (
                <Tooltip title={t('common:ui.tooltips.editSelectedExpense')}>
                  <IconButton
                    color="primary"
                    onClick={() => openEdit(table.getSelectedRowModel().flatRows[0].original, false)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDeleteBulk && (table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
                <Tooltip title={t('common:ui.tooltips.deleteSelection')}>
                  <IconButton color="error" onClick={() => requestSelectionDelete(table)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
              <ExportMenuButton
                prefix="expenses"
                data={data}
                columns={columns.filter((column) => column.accessorKey !== 'batch')}
                table={table}
              />
            </Box>
          )}
          renderRowActionMenuItems={(row, closeMenu) => [
            canEdit ? (
              <MenuItem
                key="edit"
                onClick={() => {
                  openEdit(row.original, false);
                  closeMenu();
                }}
                sx={{ color: 'primary.main' }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}><Edit fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.edit')}
              </MenuItem>
            ) : null,
            isSharedBatch(row.original) ? (
              <MenuItem
                key="view-batch"
                onClick={() => {
                  setToViewBatch(row.original.batch as string);
                  closeMenu();
                }}
              >
                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                {t('common:expense.viewBatch')}
              </MenuItem>
            ) : null,
            canEdit && isSharedBatch(row.original) ? (
              <MenuItem
                key="edit-batch"
                onClick={() => {
                  openEdit(row.original, true);
                  closeMenu();
                }}
                sx={{ color: 'primary.main' }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}><Edit fontSize="small" /></ListItemIcon>
                {t('common:expense.editBatch')}
              </MenuItem>
            ) : null,
            canDelete ? (
              <MenuItem
                key="delete"
                onClick={() => {
                  requestRowDelete(row.original);
                  closeMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Delete fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.delete')}
              </MenuItem>
            ) : null,
          ]}
        />
      </LocalizationProvider>

      <ExpenseModal
        isOpen={postOpen}
        expense={editing}
        batchMode={batchEdit}
        onClose={() => { setPostOpen(false); setEditing(null); setBatchEdit(false); }}
        onSuccess={() => void fetchData()}
      />

      {toView && (
        <ExpenseReport
          isOpen={!!toView}
          toggle={() => setToView(null)}
          bundle={toView}
        />
      )}

      {toViewBatch && (
        <ExpenseBatchReport
          isOpen={!!toViewBatch}
          toggle={() => setToViewBatch(null)}
          batch={toViewBatch}
        />
      )}

      <ConfirmDialog
        onConfirm={toDelete ? handleDelete : handleBulkDelete}
        onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]); }}
        isOpen={isConfirmOpen}
        setIsOpen={() => {}}
        title={<><DeleteOutline sx={{ color: 'red' }} /> {t('common:dialog.deleteConfirmation')}</>}
        message={
          toDelete ? (
            <>
              {t('common:expense.confirm.deleteSingle')}&nbsp;
              <b><u>{Util.formatNumber(toDelete.amount)}</u></b>&nbsp;
              {t('common:expense.confirm.deleteSinglePayee')} <b><u>{toDelete.payee}</u></b>
            </>
          ) : (
            <>{t('common:expense.confirm.deleteBulk', { count: Number(toDeleteBulk.length) })}</>
          )
        }
        confirmBtnColor="error"
        confirmBtnText={t('common:ui.buttons.delete')}
      />
    </>
  );
};

export default Expense;
